// internal deps
import { Some, Option, None } from "@swiggy-private/js-fp";
import { isNetworkError } from "@swiggy-private/core-ui";
import { ApiMenuCartItem, NativeLocalCart } from "@swiggy-private/cart-types";
import { isEmpty, isPlainObject } from "@swiggy-private/js-utils";

// utils
import { showNativeAlert } from "@cart-client/utils/Native";

// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";

// constants
import { OOPS_SOMETHING_WENT_WRONG } from "@cart-client/constants/TextDict";

export function isApiResponse<T>(response: unknown): response is ApiResponse<T> {
    return isPlainObject(response) && "statusCode" in response;
}

export function showApiError(err: unknown): void {
    let message: Option<string> = None;
    let title: Option<string> = None;

    if (isApiResponse(err)) {
        message = Some(err.statusMessage || null);
        title = Some(err.statusTitle);
    } else if (isNetworkError(err)) {
        title = Some("CONNECTION ERROR");
        message = Some("Please check your network connectivity and try again");
    }

    showNativeAlert({
        message: message.unwrapOr(OOPS_SOMETHING_WENT_WRONG),
        title: title.unwrapOr(" "),
    });
}

let nativeNetworkErrorAlertVisible = false;

export function showNetworkError(err: unknown): void {
    if (!isNetworkError(err) || nativeNetworkErrorAlertVisible) {
        return;
    }

    nativeNetworkErrorAlertVisible = true;
    showNativeAlert({
        title: "CONNECTION ERROR",
        message: "Please check your network connectivity and try again",
    })
        .then(_res => {
            nativeNetworkErrorAlertVisible = false;
        })
        .catch(_err => {
            nativeNetworkErrorAlertVisible = false;
        });
}

/* eslint-disable @typescript-eslint/naming-convention */
// convert millisecs epoch value to sec epoch value temporarily to unblock backend. Revert after BE discussion regarding this
export function getUpdateCartParams(params: NativeLocalCart): NativeLocalCart {
    // Strip cart_prefetched_response (webview-only metadata from native); posting it back
    // trips the WAF on embedded HTML/CSS and returns 403.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { cart_prefetched_response, ...cleanParams } = params as Record<string, unknown>;
    params = cleanParams as NativeLocalCart;

    if (params.date && String(params.date).length === 13) {
        params.date = Math.floor(params.date / 1000);
    }

    // logic to handle customer_request_messages based on item qty

    if (params?.cart_items) {
        const updatedCartItems = params.cart_items.map((item: ApiMenuCartItem) => {
            if (!isEmpty(item?.customer_request_messages)) {
                item.customer_request_messages = item.customer_request_messages.slice(0, item.quantity) || [];
            }
            return item;
        });
        return { ...params, cart_items: updatedCartItems };
    }

    return params;
}
/* eslint-enable @typescript-eslint/naming-convention */
